import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Box, TextField, Typography, Snackbar, Alert } from '@mui/material';
import { setParents } from '../redux/taskSlice';

import { API_URL, apiFetch } from "../config";

const ParentManager = () => {
    const [newParent, setNewParent] = useState('');
    const [feedback, setFeedback] = useState({ message: '', severity: '', open: false });
    const parents = useSelector((state) => state.tasks.parents);
    const dispatch = useDispatch();

    const fetchParents = async () => {
        try {
            const response = await apiFetch(`${API_URL}/tasks/parents`);
            const data = await response.json();
            if (response.ok) {
                dispatch(setParents(data.map((parent) => parent.name)));
            } else {
                throw new Error(data.error || 'Failed to fetch parents.');
            }
        } catch (error) {
            setFeedback({ message: error.message, severity: 'error', open: true });
        }
    };

    const handleAddParent = async () => {
        if (!newParent.trim()) {
            setFeedback({ message: 'Parent name cannot be empty.', severity: 'warning', open: true });
            return;
        }

        try {
            const response = await apiFetch(`${API_URL}/tasks/parents`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newParent }),
            });

            if (response.ok) {
                await fetchParents(); // Refresh the parent list
                setNewParent('');
                setFeedback({ message: 'Parent added successfully!', severity: 'success', open: true });
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add parent.');
            }
        } catch (error) {
            setFeedback({ message: error.message, severity: 'error', open: true });
        }
    };

    const handleDeleteParent = async (parentName) => {
        try {
            const response = await apiFetch(`${API_URL}/tasks/parents/${parentName}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await fetchParents(); // Refresh the parent list
                setFeedback({ message: 'Parent deleted successfully!', severity: 'success', open: true });
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete parent.');
            }
        } catch (error) {
            setFeedback({ message: error.message, severity: 'error', open: true });
        }
    };

    const handleCloseSnackbar = () => setFeedback({ ...feedback, open: false });

    return (
        <Box>
            {/* Feedback Snackbar */}
            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={feedback.severity} sx={{ width: '100%' }}>
                    {feedback.message}
                </Alert>
            </Snackbar>

            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <TextField
                    label="New Parent Category"
                    value={newParent}
                    onChange={(e) => setNewParent(e.target.value)}
                    fullWidth
                />
                <Button variant="contained" color="primary" onClick={handleAddParent}>
                    Add
                </Button>
            </Box>
            {parents.length > 0 ? (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Parent Categories
                    </Typography>
                    {parents.map((parent) => (
                        <Box
                            key={parent}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                            p={1}
                            sx={{
                                border: '1px solid',
                                borderColor: 'primary.main',
                                borderRadius: '4px',
                            }}
                        >
                            <Typography>{parent}</Typography>
                            <Button
                                color="secondary"
                                onClick={() => handleDeleteParent(parent)}
                                disabled={parent === 'General'} // Prevent deleting the default parent
                            >
                                Delete
                            </Button>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography>No parent categories available.</Typography>
            )}
        </Box>
    );
};

export default ParentManager;
